import { useQuery } from '@tanstack/react-query';
import { ListPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import { groupsOf } from '@/api/auth';
import { questionSetQuery } from '@/api/questionSets';
import { useSession } from '@/app/session';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table } from '@/components/ui/Table';
import { cn } from '@/lib/cn';
import NotFound from '@/pages/NotFound';
import { type } from '@/theme/typography';

import { QuestionDialog } from './QuestionDialog';
import { QUESTIONS_PATH, readSetFilters, setSearch } from './questionBankUrls';
import { mayWriteQuestionSet, setAuthorSentence, type Viewer } from './setAccess';
import { SetItems } from './SetItems';

/**
 * `DESIGN.md` §6.9 — **one question set and the questions it owns**, at `/questions/sets/:setId`.
 *
 * ⚠️ **This screen is where every question act in the product now lives** (2026-09-24, ADR 0019 — *a
 * set owns its questions*), which is exactly what the user asked for: *"there should be only
 * question sets, and adding, removing and so on should be done inside each set"* (translated; ADR 0019
 * quotes the original). Writing a question,
 * editing it, removing it, restoring it and ordering them all happen here, and nowhere else. A
 * question belongs to exactly one set; two sets holding the same wording hold **two questions**, and
 * editing one does not change the other — so nothing on this screen may say a wording is shared,
 * reused or *"in N sets"*.
 *
 * ⚠️ **What this replaces, named so that none of it reads as mislaid, and both wordings are
 * retracted rather than merely unused.** *Add question* wrote into a tenant-wide library and a second
 * control — *"pick from the library"* (`AddToSetDialog`) — put one of those entries into this set.
 * Both go with the library: `POST …/question-sets/{id}/questions/`
 * takes a **wording**, and the row it creates is this set's.
 *
 * ⚠️ **No group gate, and the rows are the server's business.** All three groups may call
 * `GET /api/v1/question-sets/{id}/`; what differs is **which sets** answer at all, and a set outside
 * this reader's reach answers **404** — byte-identical to one that never existed, because 404 comes
 * before 403 on this surface. So a set somebody sends a colleague a link to and the colleague may not
 * read renders §8's Not-found state, which is the same answer an unknown address gets and tells them
 * nothing about whether the set exists. ⚠️ **Since ADR 0019 that one predicate governs the questions
 * too**: a question is reachable only *through* its set, so the set's reach is the question's reach and
 * there is no second definition of it anywhere.
 *
 * ⚠️ **Every write on this screen is the set's author's or an `Admin`'s, asked once** — composing a
 * set is writing the **set** and not the questions in it (ADR 0016 decision 5(c)) — so the actions
 * column is all rows or none rather than a column that flickers per row. ~~a `Recruiter` may put a
 * colleague's question into their own set~~ is retracted with the library it described. §5 decides the
 * shape: **hidden, never disabled**. The client gate is never the evidence; the server refuses a
 * non-author itself.
 *
 * ⚠️ **The sentence about interviews already arranged is on this screen and renders for everybody**,
 * not only for whoever may write. It is the register of §6.9's two ADR 0004 sentences and it is owed
 * for the same reason: the failure in an interface is a **false reassurance rather than a missing one**,
 * and a reader who reorders a set without being told would reasonably assume the interview they
 * arranged this morning follows along. It does not.
 *
 * ⚠️ **The composition is read whole and *Show archived* filters what is held** (`questionSetQuery`).
 * The toggle is in the **address** (`?archived=1`), so a composition showing its archived rows survives
 * a reload, is linkable and is undone by Back; it costs no request, because the read already carries
 * `include_archived=true` — which it must, since the reorder body is a permutation over the whole
 * composition and one built from a narrower read is a 422.
 */
export default function SetScreen() {
  const session = useSession();
  const params = useParams();

  /**
   * ⚠️ **A non-numeric id makes no request at all.** `/questions/sets/abc` is an address somebody typed;
   * asking the API about it would be a 422 rendered as an error state, and what it really is is an
   * address that names no record — §8's Not-found, which is what an unknown address gets.
   */
  const setId = Number(params.setId);

  if (!Number.isInteger(setId) || setId <= 0) return <NotFound />;
  if (!session.data) return <LoadingSet />;

  return (
    <Set setId={setId} viewer={{ id: session.data.user.id, groups: groupsOf(session.data) }} />
  );
}

function Set({ setId, viewer }: { setId: number; viewer: Viewer }) {
  const { t } = useTranslation('questions');
  const [searchParams, setSearchParams] = useSearchParams();
  const [adding, setAdding] = useState(false);

  const filters = readSetFilters(searchParams);
  const set = useQuery(questionSetQuery(viewer.id, setId));

  if (set.error !== null) {
    /**
     * ⚠️ **A 404 here is *"there is nothing at this address"*, and it is deliberately not §8's Forbidden
     * state.** The three cases behind it — a set of another tenant, a set outside this reader's reach
     * and an id that never existed — are indistinguishable by design, and *"You do not have access to
     * this candidate."* over a set would be a sentence about somebody nobody named
     * (`CANDIDATE_SCOPED_RECORD` covers the candidate surface alone, on purpose).
     */
    if (set.error.isNotFound) return <NotFound />;

    return (
      <div>
        <BackToSets />
        <h1 className={type.pageTitle}>{t('sets.detail.fallbackTitle')}</h1>
        <div className="mt-10">
          <ApiErrorState
            error={set.error}
            message={t('sets.detail.error')}
            onRetry={() => void set.refetch()}
          />
        </div>
      </div>
    );
  }

  if (set.data === undefined) return <LoadingSet />;

  const detail = set.data;
  const canWrite = mayWriteQuestionSet(detail, viewer);

  /** Everything the set holds, in `position` order — the reorder's subject and the toggle's. */
  const held = detail.questions;
  const visible = filters.showArchived ? held : held.filter((question) => question.is_active);

  /**
   * ⚠️ **Whether a gap is *visible* — which is a different question from *"does an archived row
   * exist?"*, and asking the second one is what BUG-096's first fix got wrong** _(re-fixed 2026-09-25
   * after a live pass)_. The sentence below ends *"so the numbering below skips them."*, so it must not
   * render where nothing is skipped, and a `held.some((q) => !q.is_active)` guard renders in two states
   * a reader reaches in a handful of clicks: an archived row moved to **last**, where the visible rows
   * read `1, 2, 3` and nothing is missing, and **every** row archived, where there is no table at all
   * and the paragraph describes a grid that is not on screen, above §8's *"Every question in this set
   * is archived."*
   *
   * The gap is visible exactly when an archived question sits **before the last visible active one**:
   * the rows are rendered in `position` order, so anything archived after the last active row takes its
   * number off the end, where no gap can be seen. With nothing active at all `lastActive` is `-1`, no
   * index precedes it, and the sentence is suppressed — which is the all-archived state, handled by the
   * same arithmetic rather than by a second condition.
   */
  const lastActive = held.reduce(
    (last, question, index) => (question.is_active ? index : last),
    -1,
  );
  const numberingSkips =
    !filters.showArchived &&
    held.some((question, index) => !question.is_active && index < lastActive);

  return (
    <div>
      <BackToSets />

      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        {/* §9's one `h1`, and it is the set's own name — the heading is the payload's, which is why
            every state above renders something rather than nothing. */}
        <h1 className={cn(type.pageTitle, '[overflow-wrap:anywhere]')}>{detail.name}</h1>
        {/*
         * §5: hidden, never disabled — a reader who may not write this set is offered no control.
         * ⚠️ **One *Add question* control on screen at a time**, which is §6.8's rule and TASK-072 paid
         * for it: here when the set holds rows, and as §8's *"No questions in this set yet."* action
         * when it holds none. It is deliberately not offered in the all-archived state — rows are held
         * there, so the header carries it.
         */}
        {canWrite && held.length > 0 && <Button onClick={() => setAdding(true)}>{t('add')}</Button>}
      </div>

      {/*
       * ⚠️ **Two sentences, because here the author line is a sentence rather than a cell** (BUG-095,
       * 2026-09-25). It read *"Written by Nobody — it came with this workspace · 3 active questions in
       * this set."*: ADR 0016 decision 5's wording was written for the sets table, where an *Author*
       * column and its neighbours supply the grammar, and on this screen it stood alone and named a
       * person called Nobody before it explained itself. The **state** it reports is unchanged and is
       * owed — a set the ADR 0018 migration carried across has no author, and saying nothing here would
       * leave a reader to guess between *"nobody wrote it"* and *"the name did not load"*.
       *
       * ⚠️ **The sentence has its own catalogue key, and the first fix's *"it stays the cell's key"* is
       * struck** _(2026-09-25, same bug, one round later)_. Holding one key made the **cell** wrong
       * instead: a full punctuated sentence under a *Written by* header, which a live pass measured
       * reading *"Written byThis set came with the workspace and has no author."* once the table
       * restacks into label/value pairs below `lg`. What must not drift is the **decision** about what
       * `author_name: null` is, and that lives in `setAccess.ts`, where both helpers branch on it — two
       * wordings for a cell and a sentence is ordinary i18n.
       */}
      <p className={cn(type.body, 'mt-1')}>
        {setAuthorSentence(
          detail,
          (author) => t('sets.detail.writtenBy', { author }),
          t('sets.detail.noAuthor'),
        )}{' '}
        {t('sets.detail.lead', { count: detail.active_question_count })}
      </p>

      {/*
       * ⚠️ **ADR 0004's cost, paid on this screen and in the register §6.9 uses for it** — feedback
       * protection #1. Adding, removing and reordering all change what interviews arranged **from now
       * on** will ask, and change no interview that exists: its scores and their positions were
       * materialised when it was arranged, each with the wording frozen beside it. It renders for every
       * reader rather than only for whoever may write, because the reassurance is about the data and
       * not about the control — and it is a sentence rather than a tooltip, which is the whole of what
       * a client owes protection #1 (§6.9).
       */}
      <div className="mt-4 rounded border border-rule-control bg-paper-sunken p-3 text-sm text-ink-700">
        <p className="font-medium text-ink-900">{t('sets.detail.frozen')}</p>
        <p className="mt-1">{t('sets.detail.fromNow')}</p>
      </div>

      {/*
       * ⚠️ **The toggle is on screen only once there is something to reveal, and it is offered to every
       * reader.** It reveals rows rather than hiding them, which is why it is not a filter in the sense
       * §6.3's are — and it is not a write, so it carries no gate: somebody who may not touch this set
       * is still entitled to see what it used to ask. A set with nothing in it has nothing to reveal,
       * and §8's *"No questions in this set yet."* is one sentence and one action rather than a
       * sentence under a control that can change nothing.
       */}
      {held.length > 0 && (
        <div className="mt-6">
          <Checkbox
            id="set-show-archived"
            label={t('sets.items.showArchived')}
            checked={filters.showArchived}
            onCheckedChange={(checked) =>
              setSearchParams(setSearch({ showArchived: checked === true }))
            }
          />
          {/*
           * ⚠️ **The numbering gap, said rather than left to be guessed** (BUG-096, 2026-09-25). With
           * *Show archived* off the visible rows carry their **real** `position`, so a set holding an
           * archived row at 3 reads `1, 2, 4, 5` — and the gap appears only when the archived row is
           * not last, which is why a reader meets it without having done anything to cause it. That
           * *"only"* is the **condition** and not a remark about it: `numberingSkips` above is what
           * decides, and the three states where the sentence would be a falsehood are suppressed there.
           *
           * ⚠️ **Renumbering the visible rows `1..N` was refused, and the refusal is the reason this
           * sentence exists.** `position` is what ADR 0004's freeze reads and what *Restore* puts a
           * question back to — a live pass measured a row archived at 3, a reorder moving it to 4, and
           * *Restore* returning it to **4** — so consecutive numbering would make the screen show one
           * number and act on another.
           *
           * ⚠️ **It names no count.** *"1 question is hidden"* would be a second figure to keep true on
           * every filter change and every write; what a reader needs is that the places are **kept**,
           * not how many of them are. Being count-free it needs no `_one`/`_other`, and the guard that
           * admits a counted key only with a plural form is unbothered by a key that has none
           * (`src/i18n/i18n.test.tsx`).
           *
           * It is information rather than a fault — §6.9's register for a state it wants read — so no
           * `role="alert"`, no `negative`, and no control: *Show archived* is the control, immediately
           * above it.
           */}
          {numberingSkips && (
            <p className={cn(type.metadata, 'mt-2')}>{t('sets.items.numbering')}</p>
          )}
        </div>
      )}

      <div className="mt-4">
        <SetItems
          setId={setId}
          questions={held}
          visible={visible}
          hidesArchived={!filters.showArchived}
          canWrite={canWrite}
          empty={
            /*
             * ⚠️ **§8's two empty states here, and this screen can tell them apart without asking
             * twice**: it compares the rows it **shows** against the rows it **holds**, so *"rows held
             * and none shown"* is a fact rather than a guess. The only thing that can be hiding rows is
             * *Show archived* being off, which means every question in this set is archived — and the
             * one action that helps is the one that reveals them.
             */
            held.length === 0 ? (
              <EmptyState
                icon={ListPlus}
                message={t('sets.detail.empty')}
                action={
                  canWrite ? <Button onClick={() => setAdding(true)}>{t('add')}</Button> : undefined
                }
              />
            ) : (
              <EmptyState
                icon={ListPlus}
                message={t('sets.items.allArchived')}
                action={
                  <Button
                    variant="secondary"
                    onClick={() => setSearchParams(setSearch({ showArchived: true }))}
                  >
                    {t('sets.items.showArchivedAction')}
                  </Button>
                }
              />
            )
          }
        />
      </div>

      {/* Mounted only while open, so a half-typed question cannot survive a refetch and the form's
          defaults are always empty. With no `question` prop it is the **add** path, which is what
          decides that ADR 0004's sentence is not rendered there — see `QuestionDialog`. */}
      {adding && <QuestionDialog setId={setId} onClose={() => setAdding(false)} />}
    </div>
  );
}

/**
 * The way back to the list this screen was opened from — and **the one link out of it**. A set *is*
 * nested under `/questions`, so a reader who arrived by link needs a way into the list rather than only
 * the browser's Back.
 *
 * ⚠️ **It points at the bare address**, because `?tab=sets` is gone with the tabs (ADR 0019) and
 * `/questions` **is** the sets list.
 */
function BackToSets() {
  const { t } = useTranslation('questions');

  return (
    <Link
      to={QUESTIONS_PATH}
      className={cn(type.metadata, 'mb-2 inline-block underline-offset-2 hover:underline')}
    >
      {t('sets.detail.back')}
    </Link>
  );
}

/** §8's loading state: rows shaped like rows (`Table` owns the shape), never a centred spinner. */
function LoadingSet() {
  const { t } = useTranslation('questions');

  return (
    <div>
      <BackToSets />
      <h1 className={type.pageTitle}>{t('sets.detail.fallbackTitle')}</h1>
      <div className="mt-6">
        <Table columns={[]} rows={[]} rowKey={() => 0} isLoading />
      </div>
    </div>
  );
}
